import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { saveChecklist, getAllChecklistCategories } from "src/pages/api";

const FIELD_TYPES = [
  "TextBox",
  "Drop Down",
  "Checkbox",
  "Radio Button",
  "Date",
  "Embed Video",
];

const FIELD_TYPE_MAP = {
  "TextBox": "text",
  "Drop Down": "dropdown",
  "Checkbox": "checkbox",
  "Radio Button": "radio",
  "Date": "date",
  "Embed Video": "embedVideo",
};

const YOUTUBE_REGEX = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]+/;

const schema = Yup.object().shape({
  name: Yup.string().trim().required("Checklist name is required"),
  type: Yup.string().trim().required("Type is required"),
  categoryId: Yup.string().trim().required("Category is required"),
  notifyEmails: Yup.string()
    .trim()
    .nullable()
    .test("emails-valid", "Enter valid email address(es), comma separated", (value) => {
      if (!value) return true;
      return value
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean)
        .every((e) => Yup.string().email().isValidSync(e));
    }),
  requireSignature: Yup.boolean(),
  fields: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().trim().required("Field name is required"),
      fieldType: Yup.string().trim().required("Field type is required"),
      imageRequired: Yup.boolean(),
      imageCount: Yup.number().nullable(),
      optionsText: Yup.string().nullable(),
      videoLink: Yup.string()
        .nullable()
        .when("fieldType", {
          is: "Embed Video",
          then: (s) =>
            s
              .required("YouTube link is required")
              .test("single-link", "Only one video link is allowed", (val) =>
                !val ? false : !/[\s,]/.test(val.trim()),
              )
              .test("is-youtube", "Only YouTube links are allowed", (val) =>
                !val ? false : YOUTUBE_REGEX.test(val.trim()),
              ),
        }),
    }),
  ),
});

const defaultValues = {
  name: "",
  type: "Open with Only QR Code",
  categoryId: "",
  notifyEmails: "",
  requireSignature: false,
  fields: [],
};

function FieldPreview({ fieldType, optionsText }) {
  const options = String(optionsText || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  if (!options.length) return null;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "grey.100",
        border: "1px dashed",
        borderColor: "grey.400",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
        Preview
      </Typography>

      {fieldType === "Drop Down" && (
        <FormControl size="small" fullWidth>
          <Select displayEmpty defaultValue="" size="small">
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {options.map((opt, i) => (
              <MenuItem key={i} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {fieldType === "Radio Button" && (
        <FormControl>
          <FormLabel sx={{ fontSize: 12 }}>Options</FormLabel>
          <RadioGroup>
            {options.map((opt, i) => (
              <FormControlLabel
                key={i}
                value={opt}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{opt}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {fieldType === "Checkbox" && (
        <FormControl>
          <FormLabel sx={{ fontSize: 12 }}>Options</FormLabel>
          <Stack>
            {options.map((opt, i) => (
              <FormControlLabel
                key={i}
                control={<Checkbox size="small" />}
                label={<Typography variant="body2">{opt}</Typography>}
              />
            ))}
          </Stack>
        </FormControl>
      )}
    </Box>
  );
}

export default function CreateChecklist({ onCreated }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [emailInputValue, setEmailInputValue] = React.useState("");

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { control, handleSubmit, reset, watch } = methods;

  // Fetch all checklist categories for dropdown
  const { data: categoriesData } = useQuery({
    queryKey: ["checklist-categories", 0, 100, ""],
    queryFn: getAllChecklistCategories,
  });
  const categories = categoriesData?.data || [];

  const { fields, append, remove } = useFieldArray({
    control,
    name: "fields",
  });

  const mutation = useMutation(
    async (formData) => {
      const normalizedFields = (formData.fields || []).map((f) => {
        const rawOptions = String(f.optionsText || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        const needsOptions =
          f.fieldType === "Drop Down" ||
          f.fieldType === "Radio Button" ||
          f.fieldType === "Checkbox";

        const isVideo = f.fieldType === "Embed Video";

        return {
          name: f.name?.trim(),
          fieldType: FIELD_TYPE_MAP[f.fieldType] || f.fieldType,
          imageRequired: !!f.imageRequired,
          imageCount: f.imageRequired ? Number(f.imageCount || 3) : 0,
          options: isVideo
            ? (f.videoLink?.trim() ? [f.videoLink.trim()] : [])
            : needsOptions ? rawOptions : [],
        };
      });

      const payload = {
        name: formData.name?.trim(),
        categoryId: formData.categoryId,
        type: formData.type,
        isActive: true,
        notifyEmails: String(formData.notifyEmails || "")
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean)
          .join(","),
        requireSignature: formData.requireSignature ? 1 : 0,
        fields: normalizedFields,
      };

      return saveChecklist({ req: payload });
    },
    {
      onSuccess: () => {
        enqueueSnackbar("Checklist saved successfully", { variant: "success" });
        queryClient.invalidateQueries(["checklists"]);
        reset(defaultValues);
        setEmailInputValue("");
        onCreated?.();
      },
      onError: (error) => {
        const apiMessage =
          error?.response?.data?.details ||
          error?.response?.data?.error ||
          error?.response?.data?.message;
        enqueueSnackbar(apiMessage || error?.message || "Failed to save checklist", {
          variant: "error",
        });
      },
    },
  );

  const currentFields = watch("fields") || [];

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Card sx={{ p: { xs: 2, md: 4 } }}>
        <Stack spacing={2}>
          <Typography variant="h5" textAlign="center" fontWeight={700}>
            Create Checklist
          </Typography>

          <Box component="form" onSubmit={handleSubmit((d) => mutation.mutate(d))}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Checklist Name"
                      error={!!error}
                      helperText={error?.message}
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                    >
                      <MenuItem value="Open with Only QR Code">
                        Open with Only QR Code
                      </MenuItem>
                      <MenuItem value="Open without QR Code">
                        Open without QR Code
                      </MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Category"
                      error={!!error}
                      helperText={error?.message}
                      size="small"
                    >
                      <MenuItem value="">Select Category</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="notifyEmails"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const emails = String(field.value || "")
                      .split(",")
                      .map((e) => e.trim())
                      .filter(Boolean);

                    return (
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={emails}
                        inputValue={emailInputValue}
                        onInputChange={(_, newInputValue) => setEmailInputValue(newInputValue)}
                        onChange={(_, newValue) =>
                          field.onChange(
                            newValue
                              .map((e) => String(e).trim())
                              .filter(Boolean)
                              .join(", "),
                          )
                        }
                        onBlur={(e) => {
                          const pending = e.target.value?.trim();
                          if (pending) {
                            field.onChange([...emails, pending].join(", "));
                          }
                          field.onBlur();
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              size="small"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Notify Emails"
                            placeholder={emails.length ? "" : "e.g. abc@example.com"}
                            error={!!error}
                            helperText={
                              error?.message || "Press Enter or comma to add an email"
                            }
                            size="small"
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="requireSignature"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Require Signature"
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                Add Field
              </Typography>

              <Stack spacing={2}>
                {fields.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      p: 2,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Controller
                          name={`fields.${index}.name`}
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              fullWidth
                              placeholder="Field Name"
                              size="small"
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Controller
                          name={`fields.${index}.fieldType`}
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              size="small"
                              error={!!error}
                              helperText={error?.message}
                            >
                              {FIELD_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>

                      {(currentFields[index]?.fieldType === "Drop Down" ||
                        currentFields[index]?.fieldType === "Radio Button" ||
                        currentFields[index]?.fieldType === "Checkbox") && (
                        <Grid item xs={12}>
                          <Controller
                            name={`fields.${index}.optionsText`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  label="Values"
                                  placeholder="e.g. Option1, Option2, Option3"
                                  helperText="Multiple values comma separated"
                                />
                                <FieldPreview
                                  fieldType={currentFields[index]?.fieldType}
                                  optionsText={field.value}
                                />
                              </>
                            )}
                          />
                        </Grid>
                      )}

                      {currentFields[index]?.fieldType === "Embed Video" && (
                        <Grid item xs={12}>
                          <Controller
                            name={`fields.${index}.videoLink`}
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                              <>
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  label="YouTube Link"
                                  placeholder="https://www.youtube.com/watch?v=..."
                                  error={!!error}
                                  helperText={
                                    error?.message || "Only YouTube links are allowed"
                                  }
                                />
                                {field.value && YOUTUBE_REGEX.test(field.value) && (
                                  <Box
                                    sx={{
                                      mt: 1,
                                      p: 1,
                                      borderRadius: 1,
                                      bgcolor: "grey.100",
                                      border: "1px dashed",
                                      borderColor: "grey.400",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight={600}
                                      display="block"
                                      sx={{ mb: 0.5 }}
                                    >
                                      Preview
                                    </Typography>
                                    <Box
                                      component="iframe"
                                      src={field.value
                                        .replace("watch?v=", "embed/")
                                        .replace("youtu.be/", "www.youtube.com/embed/")
                                        .replace("youtube.com/shorts/", "www.youtube.com/embed/")}
                                      width="100%"
                                      height="180"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                      sx={{ borderRadius: 1 }}
                                    />
                                  </Box>
                                )}
                              </>
                            )}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Controller
                          name={`fields.${index}.imageRequired`}
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                />
                              }
                              label="Image Required"
                            />
                          )}
                        />
                      </Grid>


                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => remove(index)}
                        >
                          Remove Field
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    append({
                      name: "",
                      fieldType: "TextBox",
                      imageRequired: false,
                      imageCount: 3,
                      optionsText: "",
                    })
                  }
                >
                  + Add Field
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  type="submit"
                  disabled={mutation.isLoading}
                >
                  Save Checklist
                </Button>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Card>
    </Container>
  );
}

